import React, { useState, useCallback, useEffect } from 'react';
import texasApi from '../../services/texasApi';
import { landlordApi } from '../../services/api';
import ExperimentSignificance from './experiments/ExperimentSignificance';

export const TexasAnalytics: React.FC = () => {
  const [portfolio, setPortfolio] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await texasApi.complianceDashboard.getPortfolio();
      setPortfolio(res);
    } catch (err: any) {
      try {
        const res = await landlordApi.get('api/texas/compliance-dashboard/portfolio');
        const d = res?.data?.data || res?.data;
        setPortfolio(d);
      } catch {
        setError('Failed to load analytics');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const propertyCount = portfolio?.propertyCount ?? 0;
  const registrationStatus = portfolio?.registrationStatus ?? {};
  const score = portfolio?.score ?? 0;
  const expiringCount = portfolio?.expiringCount ?? 0;
  const expiredCount = portfolio?.expiredCount ?? 0;

  const activeRegistrations = registrationStatus.active ?? 0;
  const expiringRegistrations = registrationStatus.expiring ?? 0;
  const expiredRegistrations = registrationStatus.expired ?? 0;

  return (
    <div data-testid="texas-analytics" className="texas-page-container">
      <h1>Texas Analytics</h1>
      <p className="page-description">
        Portfolio analytics — compliance overview, registration status, and risk metrics.
      </p>

      {error && <div className="error" data-testid="error">{error}</div>}

      {!loading && (
        <section className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="text-xs font-bold text-slate-500 tracking-wider">PROPERTIES</div>
            <div className="text-3xl font-black text-slate-900 mt-1">{propertyCount}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="text-xs font-bold text-emerald-600 tracking-wider">SCORE</div>
            <div className="text-3xl font-black text-emerald-600 mt-1">{score}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="text-xs font-bold text-amber-600 tracking-wider">EXPIRING</div>
            <div className="text-3xl font-black text-amber-600 mt-1">{expiringCount}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="text-xs font-bold text-red-600 tracking-wider">EXPIRED</div>
            <div className="text-3xl font-black text-red-600 mt-1">{expiredCount}</div>
          </div>
        </section>
      )}

      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4">
        <h2>Registration Status</h2>
        <div className="space-y-2">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="font-bold text-gray-900 text-xs">Active</span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">{activeRegistrations}</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="font-bold text-gray-900 text-xs">Expiring Soon</span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">{expiringRegistrations}</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="font-bold text-gray-900 text-xs">Expired</span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">{expiredRegistrations}</span>
          </div>
        </div>
      </section>

      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <h2>Compliance Summary</h2>
        {loading ? (
          <div className="no-results">Loading analytics...</div>
        ) : (
          <pre className="text-xs">{JSON.stringify(portfolio, null, 2)}</pre>
        )}
      </section>

      {/* T-164-003: experiment significance calculator (admin surface) */}
      <ExperimentSignificance />
    </div>
  );
};

export default TexasAnalytics;
